import { BookOpen, CheckCircle2, Dumbbell, Plus } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Card } from '@/components/ui/Card'
import type { TodayPlan } from '@/api/dashboard'
import { formatMessage } from '@/i18n/format'
import { useMinCopy } from '@/i18n/useUiLocale'
import { copy } from '@/lib/copy'
import { cn } from '@/components/ui/cn'

const linkBtnCls =
  'inline-flex h-8 items-center justify-center rounded-md border border-input bg-background px-3 text-sm font-medium shadow-sm hover:bg-accent transition-colors'

const primaryBtnCls =
  'inline-flex h-9 items-center justify-center gap-1.5 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90'

interface TodayPlanWidgetProps {
  plan: TodayPlan
  isNewUser: boolean
}

function ProgressBar({ value, max, color }: { value: number; max: number; color: string }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0
  return (
    <div className="mt-3 h-2.5 w-full rounded-full bg-secondary">
      <div className={`h-2.5 rounded-full transition-all ${color}`} style={{ width: `${pct}%` }} />
    </div>
  )
}

function PlanCell({
  icon,
  label,
  done,
  goal,
  color,
  cta,
  preview,
  testId,
}: {
  icon: React.ReactNode
  label: string
  done: number
  goal: number
  color: string
  cta?: React.ReactNode
  preview?: boolean
  testId?: string
}) {
  const complete = !preview && done >= goal

  return (
    <div
      className={`flex h-full flex-col rounded-xl border p-4 ${
        preview ? 'border-dashed border-border/60 bg-muted/10' : 'border-border/60 bg-muted/20'
      }`}
      data-testid={testId}
    >
      <div className="flex min-h-6 items-center justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-center gap-2.5">
          <span className="flex h-5 w-5 shrink-0 items-center justify-center [&>svg]:h-5 [&>svg]:w-5">
            {icon}
          </span>
          <span className="text-sm font-semibold leading-none">{label}</span>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <span className="text-sm font-medium tabular-nums leading-none text-muted-foreground">
            {done}/{goal}
          </span>
          {complete ? <CheckCircle2 className="h-4 w-4 shrink-0 text-green-500" /> : null}
        </div>
      </div>
      <ProgressBar value={done} max={goal} color={preview ? 'bg-muted-foreground/20' : color} />
      <div className="mt-3 flex min-h-8 items-end justify-end">{!complete && cta ? cta : null}</div>
    </div>
  )
}

export function TodayPlanWidget({ plan, isNewUser }: TodayPlanWidgetProps) {
  const { actions, todayPlan } = useMinCopy()
  const showPractice = plan.practiceGoal > 0
  const gridCols = showPractice
    ? 'md:grid-cols-2 lg:grid-cols-4'
    : 'md:grid-cols-2 lg:grid-cols-3'

  const readyToRefreshLabel =
    plan.dueReviews === 1
      ? actions.readyToRefreshOne
      : formatMessage(actions.readyToRefresh, { count: plan.dueReviews })

  const body = (
    <>
      {isNewUser ? (
        <p className="mb-4 text-sm text-muted-foreground">{copy.onboarding.todayPlanPreview}</p>
      ) : null}
      <div className={cn('grid gap-4 md:items-stretch', gridCols)}>
        <PlanCell
          icon={<CheckCircle2 className="h-5 w-5 text-blue-500" />}
          label={todayPlan.refresh}
          done={isNewUser ? 0 : plan.reviewsCompleted}
          goal={plan.reviewGoal}
          color="bg-blue-500"
          preview={isNewUser}
          cta={
            isNewUser ? (
              <Link to="/reading/recommended" className={primaryBtnCls}>
                <BookOpen className="h-4 w-4" />
                {actions.startReading}
              </Link>
            ) : plan.dueReviews > 0 ? (
              <Link to="/review" className={linkBtnCls}>
                {readyToRefreshLabel}
              </Link>
            ) : undefined
          }
        />
        <PlanCell
          icon={<Plus className="h-5 w-5 text-purple-500" />}
          label={todayPlan.newWords}
          done={isNewUser ? 0 : Number(plan.newWordsAdded)}
          goal={plan.newWordsGoal}
          color="bg-purple-500"
          preview={isNewUser}
          cta={
            isNewUser ? (
              <Link to="/reading/recommended" className={linkBtnCls}>
                {actions.startReading}
              </Link>
            ) : Number(plan.newWordsAdded) < plan.newWordsGoal ? (
              <Link to="/vocabulary" className={linkBtnCls}>
                {actions.addWords}
              </Link>
            ) : undefined
          }
        />
        <PlanCell
          icon={<BookOpen className="h-5 w-5 text-amber-500" />}
          label={todayPlan.reading}
          done={isNewUser ? 0 : plan.articlesRead}
          goal={plan.articlesGoal}
          color="bg-amber-500"
          preview={isNewUser}
          cta={
            isNewUser ? (
              <Link to="/reading" className={linkBtnCls}>
                {actions.browseReading}
              </Link>
            ) : plan.articlesRead < plan.articlesGoal ? (
              <Link to="/reading" className={linkBtnCls}>
                {actions.browseReading}
              </Link>
            ) : undefined
          }
        />
        {showPractice ? (
          <PlanCell
            icon={<Dumbbell className="h-5 w-5 text-teal-500" />}
            label={todayPlan.practice}
            done={isNewUser ? 0 : plan.practiceSetsCompleted}
            goal={plan.practiceGoal}
            color="bg-teal-500"
            preview={isNewUser}
            testId="today-plan-practice"
            cta={
              isNewUser ? (
                <Link to="/ai-practice" className={linkBtnCls}>
                  {actions.startPractice}
                </Link>
              ) : plan.practiceSetsCompleted < plan.practiceGoal ? (
                <Link to="/ai-practice" className={linkBtnCls} data-testid="today-plan-practice-cta">
                  {actions.startPractice}
                </Link>
              ) : undefined
            }
          />
        ) : null}
      </div>
    </>
  )

  return (
    <Card className="p-5">
      <p className="mb-4 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {todayPlan.heading}
      </p>
      {body}
    </Card>
  )
}
